import React from "react";
import {
  RegistrationType,
  SignupFormData,
  Gender,
} from "../model/signup-types";
import { useSignupForm } from "../hooks/useSignupForm";
import { useEmailVerification } from "../hooks/useEmailVerification";
import { validateForm } from "../utils/form-validation";
import { TermsModal } from "./TermsModal";
import { AddressSearchModal } from "./AddressSearchModal";
import { useMemberStore } from "@/features/auth/stores/member-store";
import {
  TERMS_OF_SERVICE,
  PRIVACY_POLICY,
  MARKETING_POLICY,
} from "./terms-content";

type FormMode = "signup" | "edit";

interface SignupFormProps {
  type: RegistrationType;
  onSubmit: (data: SignupFormData) => void;
  onBack?: () => void;
  mode?: FormMode;
  initialData?: Partial<SignupFormData>;
  isLoading?: boolean;
}

/**
 * 회원가입/회원정보 수정 폼 컴포넌트
 * useSignupForm hook으로 모든 상태 관리
 * validateForm 유틸로 검증 로직 처리
 */
export function SignupForm({
  type,
  onSubmit,
  onBack,
  mode = "signup",
  initialData,
  isLoading = false,
}: SignupFormProps) {
  const isEditMode = mode === "edit";
  const { user } = useMemberStore();

  // edit 모드일 때는 Zustand에서 user_type 가져오기, 아니면 prop으로 받은 type 사용
  const effectiveType: RegistrationType = isEditMode
    ? (user?.user_type as RegistrationType) || "individual"
    : type;

  const {
    formData,
    errors,
    emailSent,
    setEmailSent,
    changePassword,
    setChangePassword,
    openModal,
    setOpenModal,
    handleChange,
    setErrors,
  } = useSignupForm(effectiveType, mode, initialData);

  // 이메일 인증 hook
  const emailVerification = useEmailVerification();

  const handleSendEmail = async () => {
    // 이메일 인증 코드 발송
    const success = await emailVerification.sendVerificationEmail(
      formData.email,
      formData.username
    );

    if (success) {
      setEmailSent(true);
    } else {
      setErrors((prev) => ({
        ...prev,
        email: emailVerification.error || "이메일 발송에 실패했습니다.",
      }));
    }
  };

  const handleVerifyEmail = () => {
    // 사용자 입력 코드 검증
    const isValid = emailVerification.verifyCode(
      formData.emailVerificationCode
    );

    if (isValid) {
      handleChange("isEmailVerified", true);
      setEmailSent(false);
    } else {
      setErrors((prev) => ({
        ...prev,
        emailVerificationCode:
          emailVerification.error || "인증 코드가 일치하지 않습니다.",
      }));
    }
  };

  const handleAllAgree = () => {
    const allChecked = formData.agreeToTerms && formData.agreeToPrivacy;
    handleChange("agreeToTerms", !allChecked);
    handleChange("agreeToPrivacy", !allChecked);
    handleChange("agreeToMarketing", !allChecked);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 폼 검증 (검증 에러가 없으면 빈 객체 반환)
    const newErrors = validateForm(formData, isEditMode, changePassword, type);

    // 에러가 있으면 에러 상태 업데이트 후 제출 중단
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // 에러가 없으면 제출
    onSubmit(formData);
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-3 md:px-4 py-4 md:py-6 lg:py-8">
      {/* 제목 */}
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {isEditMode ? "회원정보 수정" : "회원가입"}
        </h1>
        <p className="text-sm md:text-base text-gray-600">
          {isEditMode
            ? "회원정보를 수정할 수 있습니다."
            : "회원정보를 입력하여 가입해 주세요."}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
        {/* 이름 */}
        <InputField
          label={
            formData.user_type === "kindergarten"
              ? "유치원명"
              : formData.user_type === "franchise"
              ? "프랜차이즈명"
              : "이름"
          }
          value={formData.username}
          onChange={(e) => handleChange("username", e.target.value)}
          error={errors.username}
          placeholder="이름을 입력해주세요"
          required={!isEditMode}
        />

        {/* 성별 */}
        <GenderSection
          value={formData.gender}
          onChange={(value) => handleChange("gender", value)}
          error={errors.gender}
          required={!isEditMode}
        />

        {/* 아이디 */}
        {!isEditMode && (
          <InputField
            label="아이디"
            value={formData.user_id}
            onChange={(e) => handleChange("user_id", e.target.value)}
            error={errors.userId}
            placeholder="아이디를 입력해주세요"
            required
          />
        )}

        {/* 비밀번호 */}
        {isEditMode ? (
          <PasswordEditSection
            changePassword={changePassword}
            password={formData.password}
            passwordConfirm={formData.passwordConfirm}
            errors={errors}
            onToggleChange={() => setChangePassword(!changePassword)}
            onPasswordChange={(e) => handleChange("password", e.target.value)}
            onPasswordConfirmChange={(e) =>
              handleChange("passwordConfirm", e.target.value)
            }
          />
        ) : (
          <PasswordSignupSection
            password={formData.password}
            passwordConfirm={formData.passwordConfirm}
            errors={errors}
            onPasswordChange={(e) => handleChange("password", e.target.value)}
            onPasswordConfirmChange={(e) =>
              handleChange("passwordConfirm", e.target.value)
            }
          />
        )}

        {/* 주소 */}
        <AddressSearchSection
          address={formData.address}
          error={errors.address}
          onAddressClick={() => setOpenModal("address")}
          required={!isEditMode}
        />

        <InputField
          label="상세주소"
          value={formData.address_detail}
          onChange={(e) => handleChange("address_detail", e.target.value)}
          error={errors.address_detail}
          placeholder="아파트/빌딩명, 호수 등을 입력해주세요"
          required={!isEditMode}
        />

        {/* 담당자 정보 (유치원, 프랜차이즈에서만 표시) */}
        {(formData.user_type === "kindergarten" ||
          formData.user_type === "franchise") && (
          <>
            <InputField
              label="담당자 이름"
              value={formData.manager_name || ""}
              onChange={(e) => handleChange("manager_name", e.target.value)}
              error={errors.manager_name}
              placeholder="담당자 이름을 입력해주세요"
              required={!isEditMode}
            />

            <InputField
              label="담당자 전화번호"
              value={formData.manager_phone || ""}
              onChange={(e) => handleChange("manager_phone", e.target.value)}
              error={errors.manager_phone}
              placeholder="010-1234-5678"
              required={!isEditMode}
            />

            <InputField
              label="사업자 번호"
              value={formData.business_number || ""}
              onChange={(e) => handleChange("business_number", e.target.value)}
              error={errors.business_number}
              placeholder="사업자 번호를 입력해주세요"
              required={!isEditMode}
            />
          </>
        )}

        {/* 휴대전화 */}
        <InputField
          label="휴대전화"
          value={formData.user_phone}
          onChange={(e) => handleChange("user_phone", e.target.value)}
          error={errors.user_phone}
          placeholder="010-1234-5678"
          required={!isEditMode}
        />

        {/* 이메일 검증 (signup 모드에서만) */}
        {!isEditMode && (
          <EmailVerificationSection
            email={formData.email}
            emailVerificationCode={formData.emailVerificationCode}
            isEmailVerified={formData.isEmailVerified}
            emailSent={emailSent}
            error={errors.email}
            verificationError={errors.emailVerificationCode}
            required={!isEditMode}
            onEmailChange={(e) => handleChange("email", e.target.value)}
            onSendEmail={handleSendEmail}
            onCodeChange={(e) =>
              handleChange("emailVerificationCode", e.target.value)
            }
            onVerifyEmail={handleVerifyEmail}
            emailVerificationTime={emailVerification.formattedTime()}
            isLoading={emailVerification.isLoading}
            failedAttempts={emailVerification.failedAttempts}
          />
        )}

        {/* 약관 동의 (signup 모드에서만) */}
        {!isEditMode && (
          <TermsAgreementSection
            agreeToTerms={formData.agreeToTerms}
            agreeToPrivacy={formData.agreeToPrivacy}
            agreeToMarketing={formData.agreeToMarketing}
            errors={errors}
            onTermsChange={(e) =>
              handleChange("agreeToTerms", e.target.checked)
            }
            onPrivacyChange={(e) =>
              handleChange("agreeToPrivacy", e.target.checked)
            }
            onMarketingChange={(e) =>
              handleChange("agreeToMarketing", e.target.checked)
            }
            onAllAgree={handleAllAgree}
            onTermsClick={() => setOpenModal("terms")}
            onPrivacyClick={() => setOpenModal("privacy")}
            onMarketingClick={() => setOpenModal("marketing")}
          />
        )}

        {/* 버튼 */}
        <FormActionButtons
          isLoading={isLoading}
          isEditMode={isEditMode}
          onBack={onBack}
        />
      </form>

      {/* 모달들 */}
      <TermsModal
        title="GGP 이용약관"
        content={TERMS_OF_SERVICE}
        isOpen={openModal === "terms"}
        onClose={() => setOpenModal(null)}
      />
      <TermsModal
        title="개인정보 수집 및 이용"
        content={PRIVACY_POLICY}
        isOpen={openModal === "privacy"}
        onClose={() => setOpenModal(null)}
      />
      <TermsModal
        title="마케팅 활용"
        content={MARKETING_POLICY}
        isOpen={openModal === "marketing"}
        onClose={() => setOpenModal(null)}
      />
      <AddressSearchModal
        isOpen={openModal === "address"}
        onComplete={(data) => {
          handleChange("address", data.address);
        }}
        onClose={() => setOpenModal(null)}
      />
    </div>
  );
}

// ============ 섹션 컴포넌트 ============

interface InputFieldProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
}

const InputField = ({
  label,
  value,
  onChange,
  error,
  placeholder,
  required,
  type = "text",
}: InputFieldProps) => (
  <div>
    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
      {label}
      {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 ${
        error
          ? "border-red-500 focus:ring-red-500"
          : "border-gray-300 focus:ring-orange-500"
      }`}
    />
    {error && <p className="text-red-500 text-xs md:text-sm mt-1">{error}</p>}
  </div>
);

interface GenderSectionProps {
  value: Gender | undefined;
  onChange: (value: Gender) => void;
  error?: string;
  required?: boolean;
}

const GenderSection = ({
  value,
  onChange,
  error,
  required,
}: GenderSectionProps) => (
  <div>
    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
      성별 {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex gap-2 md:gap-4">
      <button
        type="button"
        onClick={() => onChange("male")}
        className={`flex-1 py-2 md:py-3 text-sm md:text-base border-2 rounded-lg font-semibold transition-colors ${
          value === "male"
            ? "bg-gray-200 border-gray-400 text-gray-900"
            : "border-gray-300 text-gray-600 hover:border-gray-400"
        }`}
      >
        남성
      </button>
      <button
        type="button"
        onClick={() => onChange("female")}
        className={`flex-1 py-2 md:py-3 text-sm md:text-base border-2 rounded-lg font-semibold transition-colors ${
          value === "female"
            ? "bg-gray-200 border-gray-400 text-gray-900"
            : "border-gray-300 text-gray-600 hover:border-gray-400"
        }`}
      >
        여성
      </button>
    </div>
    {error && <p className="text-red-500 text-xs md:text-sm mt-1">{error}</p>}
  </div>
);

interface PasswordSignupSectionProps {
  password: string;
  passwordConfirm: string;
  errors: Record<string, string>;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordConfirmChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PasswordSignupSection = ({
  password,
  passwordConfirm,
  errors,
  onPasswordChange,
  onPasswordConfirmChange,
}: PasswordSignupSectionProps) => (
  <>
    <InputField
      label="비밀번호"
      type="password"
      value={password}
      onChange={onPasswordChange}
      error={errors.password}
      placeholder="비밀번호를 입력해주세요"
      required
    />
    <InputField
      label="비밀번호 확인"
      type="password"
      value={passwordConfirm}
      onChange={onPasswordConfirmChange}
      error={errors.passwordConfirm}
      placeholder="비밀번호를 다시 입력해주세요"
      required
    />
  </>
);

interface PasswordEditSectionProps {
  changePassword: boolean;
  password: string;
  passwordConfirm: string;
  errors: Record<string, string>;
  onToggleChange: () => void;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordConfirmChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const PasswordEditSection = ({
  changePassword,
  password,
  passwordConfirm,
  errors,
  onToggleChange,
  onPasswordChange,
  onPasswordConfirmChange,
}: PasswordEditSectionProps) => (
  <div className="pt-4 border-t">
    <label className="flex items-center gap-2 mb-4">
      <input
        type="checkbox"
        checked={changePassword}
        onChange={onToggleChange}
        className="w-4 h-4 rounded cursor-pointer"
      />
      <span className="text-sm font-semibold text-gray-700">비밀번호 변경</span>
    </label>

    {changePassword && (
      <div className="space-y-5 mt-4">
        <InputField
          label="새 비밀번호"
          type="password"
          value={password}
          onChange={onPasswordChange}
          error={errors.password}
          placeholder="새 비밀번호를 입력해주세요"
          required
        />
        <InputField
          label="새 비밀번호 확인"
          type="password"
          value={passwordConfirm}
          onChange={onPasswordConfirmChange}
          error={errors.passwordConfirm}
          placeholder="새 비밀번호를 다시 입력해주세요"
          required
        />
      </div>
    )}
  </div>
);

interface AddressSearchSectionProps {
  address: string;
  error?: string;
  onAddressClick: () => void;
  required?: boolean;
}

const AddressSearchSection = ({
  address,
  error,
  onAddressClick,
  required,
}: AddressSearchSectionProps) => (
  <div>
    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
      주소(대) {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex gap-2">
      <input
        type="text"
        value={address}
        readOnly
        className={`flex-1 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg bg-gray-50 cursor-pointer ${
          error ? "border-red-500" : "border-gray-300"
        }`}
        placeholder="주소를 검색해주세요"
      />
      <button
        type="button"
        onClick={onAddressClick}
        className="px-4 md:px-6 py-2 md:py-3 bg-[#FF7131] text-white text-sm md:text-base font-semibold rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
      >
        검색
      </button>
    </div>
    {error && <p className="mt-1 text-xs md:text-sm text-red-500">{error}</p>}
  </div>
);

interface EmailVerificationSectionProps {
  email: string;
  emailVerificationCode: string;
  isEmailVerified: boolean;
  emailSent: boolean;
  error?: string;
  verificationError?: string;
  required?: boolean;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendEmail: () => void;
  onCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onVerifyEmail: () => void;
  emailVerificationTime?: string;
  isLoading?: boolean;
  failedAttempts?: number;
}

const EmailVerificationSection = ({
  email,
  emailVerificationCode,
  isEmailVerified,
  emailSent,
  error,
  verificationError,
  required,
  onEmailChange,
  onSendEmail,
  onCodeChange,
  onVerifyEmail,
  emailVerificationTime,
  isLoading,
  failedAttempts = 0,
}: EmailVerificationSectionProps) => (
  <div>
    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
      이메일 {required && <span className="text-red-500">*</span>}
    </label>

    <div className="flex flex-col md:flex-row gap-2 mb-3">
      <input
        type="email"
        value={email}
        onChange={onEmailChange}
        disabled={isEmailVerified || emailSent}
        className={`flex-1 px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 disabled:bg-gray-100 ${
          error
            ? "border-red-500 focus:ring-red-500"
            : "border-gray-300 focus:ring-orange-500"
        }`}
        placeholder="이메일 주소를 입력해주세요"
      />
      <button
        type="button"
        onClick={onSendEmail}
        disabled={isEmailVerified || emailSent || !email || isLoading}
        className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-colors whitespace-nowrap ${
          isEmailVerified || emailSent || isLoading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#FF7131] text-white hover:bg-orange-600"
        }`}
      >
        {isLoading
          ? "발송 중..."
          : isEmailVerified
          ? "인증완료"
          : "메일로 보내기"}
      </button>
    </div>
    {error && <p className="mt-1 text-xs md:text-sm text-red-500">{error}</p>}

    {emailSent && !isEmailVerified && (
      <div>
        <div className="mb-3 text-xs md:text-sm text-gray-600">
          인증번호 입력 남은 시간:{" "}
          <span className="font-semibold text-orange-600">
            {emailVerificationTime}
          </span>
        </div>
        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <div className="flex-1">
            <input
              type="text"
              value={emailVerificationCode}
              onChange={onCodeChange}
              className={`w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border rounded-lg focus:outline-none focus:ring-2 ${
                verificationError
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-orange-500"
              }`}
              placeholder="인증번호를 입력해주세요"
              maxLength={8}
            />
            {verificationError && (
              <p className="mt-1 text-xs md:text-sm text-red-500">
                {verificationError}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onVerifyEmail}
            disabled={!emailVerificationCode || isLoading}
            className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-semibold rounded-lg transition-colors whitespace-nowrap ${
              !emailVerificationCode || isLoading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#FF7131] text-white hover:bg-orange-600"
            }`}
          >
            {isLoading ? "확인 중..." : "인증"}
          </button>
        </div>
        {failedAttempts >= 3 && (
          <p className="mt-2 text-xs md:text-sm text-orange-600">
            인증 실패가 반복되는 경우 위의 "메일로 보내기"를 눌러 새로운
            인증번호를 받으세요.
          </p>
        )}
      </div>
    )}

    {isEmailVerified && (
      <p className="text-xs md:text-sm text-green-500 font-semibold">
        ✓ 이메일 인증이 완료되었습니다.
      </p>
    )}
  </div>
);

interface TermsAgreementSectionProps {
  agreeToTerms: boolean;
  agreeToPrivacy: boolean;
  agreeToMarketing: boolean;
  errors: Record<string, string>;
  onTermsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPrivacyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMarketingChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAllAgree: () => void;
  onTermsClick: () => void;
  onPrivacyClick: () => void;
  onMarketingClick: () => void;
}

const TermsAgreementSection = ({
  agreeToTerms,
  agreeToPrivacy,
  agreeToMarketing,
  errors,
  onTermsChange,
  onPrivacyChange,
  onMarketingChange,
  onAllAgree,
  onTermsClick,
  onPrivacyClick,
  onMarketingClick,
}: TermsAgreementSectionProps) => (
  <div className="space-y-2 md:space-y-3 pt-3 md:pt-4 border-t">
    {/* 모두 동의하기 */}
    <button
      type="button"
      onClick={onAllAgree}
      className="w-full flex gap-2 md:gap-3 p-2 md:p-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 30 30"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="flex-shrink-0 mt-0.5 md:w-6 md:h-6"
      >
        <rect
          x="0.5"
          y="0.5"
          width="29"
          height="29"
          rx="4.5"
          fill={agreeToTerms && agreeToPrivacy ? "#FF7131" : "white"}
          stroke={agreeToTerms && agreeToPrivacy ? "#FF7131" : "#D9D9D9"}
        />
        {agreeToTerms && agreeToPrivacy && (
          <path
            d="M9 15L13 19L21 11"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
      <div className="flex flex-col gap-1 md:gap-2 text-left">
        <span className="font-semibold text-xs md:text-sm text-gray-800">
          모두 동의합니다
        </span>
        <div className="text-xs text-gray-600 leading-relaxed">
          이용약관, 개인정보 수집 및 활용(필수)/마케팅 활용(선택)에 대해 모두
          동의 합니다.
          <br />
          전체동의는 필수 및 선택정보에 대한 동의도 포함되어 있으며,
          개별적으로도 동의를 선택하실 수 있습니다. <br />
          선택 항목은 동의를 거부하시는 경우에도 서비스의 이용은 가능하십니다.
        </div>
      </div>
    </button>

    <div className="border-t"></div>

    {/* 이용약관 */}
    <div className="flex items-start">
      <input
        type="checkbox"
        id="agreeToTerms"
        checked={agreeToTerms}
        onChange={onTermsChange}
        className="mt-1 w-4 h-4 rounded cursor-pointer"
      />
      <label htmlFor="agreeToTerms" className="ml-2 text-sm text-gray-700">
        <span className="font-semibold">GGP 이용약관</span> 동의{" "}
        <span className="text-red-500">*</span>
        <button
          type="button"
          onClick={onTermsClick}
          className="ml-2 text-orange-500 hover:underline text-xs"
        >
          전체보기
        </button>
      </label>
    </div>
    {errors.agreeToTerms && (
      <p className="ml-6 text-sm text-red-500">{errors.agreeToTerms}</p>
    )}

    {/* 개인정보 수집 */}
    <div className="flex items-start">
      <input
        type="checkbox"
        id="agreeToPrivacy"
        checked={agreeToPrivacy}
        onChange={onPrivacyChange}
        className="mt-1 w-4 h-4 rounded cursor-pointer"
      />
      <label htmlFor="agreeToPrivacy" className="ml-2 text-sm text-gray-700">
        <span className="font-semibold">개인정보 수집 및 이용</span> 동의{" "}
        <span className="text-red-500">*</span>
        <button
          type="button"
          onClick={onPrivacyClick}
          className="ml-2 text-orange-500 hover:underline text-xs"
        >
          전체보기
        </button>
      </label>
    </div>
    {errors.agreeToPrivacy && (
      <p className="ml-6 text-sm text-red-500">{errors.agreeToPrivacy}</p>
    )}

    {/* 마케팅 활용 */}
    <div className="flex items-start">
      <input
        type="checkbox"
        id="agreeToMarketing"
        checked={agreeToMarketing}
        onChange={onMarketingChange}
        className="mt-1 w-4 h-4 rounded cursor-pointer"
      />
      <label htmlFor="agreeToMarketing" className="ml-2 text-sm text-gray-700">
        <span className="font-semibold">마케팅 활용</span> 동의 (선택)
        <button
          type="button"
          onClick={onMarketingClick}
          className="ml-2 text-orange-500 hover:underline text-xs"
        >
          전체보기
        </button>
      </label>
    </div>
  </div>
);

interface FormActionButtonsProps {
  isLoading: boolean;
  isEditMode: boolean;
  onBack?: () => void;
}

const FormActionButtons = ({
  isLoading,
  isEditMode,
  onBack,
}: FormActionButtonsProps) => (
  <div className="flex flex-col-reverse md:flex-row gap-3 md:gap-4 pt-4 md:pt-6">
    {onBack && (
      <button
        type="button"
        onClick={onBack}
        disabled={isLoading}
        className="flex-1 py-2 md:py-3 border-2 border-gray-300 rounded-lg font-semibold text-sm md:text-base text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isEditMode ? "취소" : "이전"}
      </button>
    )}
    <button
      type="submit"
      disabled={isLoading}
      className="flex-1 py-2 md:py-3 bg-[#FF7131] text-white rounded-lg font-semibold text-sm md:text-base hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? "처리 중..." : isEditMode ? "수정하기" : "회원가입 완료"}
    </button>
  </div>
);
